import { createInertiaApp } from "@inertiajs/react";
import { createElement, ReactNode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";

import PersistentLayout from "@/layouts/persistent_layout";

import { initializeTheme } from "@/hooks/use-appearance";
import { createI18n } from "@/i18n";
import type { SharedData } from "@/types";

// Temporary type definition, until @inertiajs/react provides one
type PageComponent = React.ComponentType & {
  layout?: (page: ReactNode) => ReactNode;
};

type ResolvedComponent = {
  default: PageComponent;
};

createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>("../pages/**/*.tsx", {
      eager: true,
    });
    const page = pages[`../pages/${name}.tsx`];
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`);
      return page;
    }

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    page.default.layout ??= (page: ReactNode) => (
      <PersistentLayout>{page}</PersistentLayout>
    );

    return page;
  },

  setup({ el, App, props }) {
    if (el) {
      const shared = props.initialPage.props as unknown as SharedData;
      const locale = String(shared.i18n?.locale ?? "en");
      document.documentElement.lang = locale;
      const app = createElement(
        I18nextProvider,
        { i18n: createI18n(locale) },
        createElement(App, props),
      );
      if (el.hasChildNodes()) {
        hydrateRoot(el, app);
      } else {
        createRoot(el).render(app);
      }
    } else {
      console.error(
        "Missing root element.\n\n" +
          "If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n" +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
      );
    }
  },
});

initializeTheme();
